/**
 * Global Unit Formatter - Centralised medical unit definitions
 * 
 * This is the SINGLE SOURCE OF TRUTH for all medical units in the application.
 * All components MUST use these functions - no inline unit strings allowed.
 * 
 * Principles:
 * - PDF-safe: No Unicode superscripts (avoids rendering as black squares)
 * - Locale-aware: Returns appropriate units for AU/US/GB/NZ
 * - Centralised: Single place to update unit formats
 */

// ============================================================================
// UNIT DEFINITIONS - Authoritative list of all supported units
// ============================================================================

export const UNITS = {
  // Blood count units (used for WBC, Lymphocytes, Platelets, Neutrophils)
  BLOOD_COUNT: 'x10^9/L',
  
  // Haemoglobin units by locale
  HEMOGLOBIN_SI: 'g/L',      // Australia, UK, NZ (SI units)
  HEMOGLOBIN_US: 'g/dL',     // United States (conventional)
  
  // Vitamin D units by locale
  VITAMIN_D_SI: 'nmol/L',    // Australia, UK, NZ (SI units)
  VITAMIN_D_US: 'ng/mL',     // United States (conventional)
  
  // B12 units by locale
  B12_SI: 'pmol/L',          // Australia, UK, NZ (SI units)
  B12_US: 'pg/mL',           // United States (conventional)
  
  // Ferritin (same across locales, uses micro symbol which is PDF-safe)
  FERRITIN: 'ug/L',          // Using 'ug' instead of 'µg' for PDF safety
  
  // Folate units
  FOLATE_SI: 'nmol/L',       // SI units
  FOLATE_US: 'ng/mL',        // Conventional
  
  // Iron units
  IRON_SI: 'umol/L',         // SI units (using 'u' for micro)
  IRON_US: 'ug/dL',          // Conventional
};

// ============================================================================
// LOCALE CONFIGURATION - Maps locales to their unit systems
// ============================================================================

const SI_LOCALES = ['AU', 'GB', 'NZ', 'CA', 'EU'];  // Canada uses SI units
const CONVENTIONAL_LOCALES = ['US'];

/**
 * Determines if a locale uses SI units
 * @param {string} locale - Country code (AU, US, GB, NZ, etc.)
 * @returns {boolean} True if locale uses SI units
 */
export const isSILocale = (locale) => {
  return SI_LOCALES.includes(locale?.toUpperCase()) || !CONVENTIONAL_LOCALES.includes(locale?.toUpperCase());
};

// ============================================================================
// VALUE CONVERSION FUNCTIONS
// ============================================================================

/**
 * Convert haemoglobin value for display based on locale
 * - Internal storage is always in g/L (SI units)
 * - US display converts to g/dL (divide by 10)
 * 
 * @param {number} valueInGL - Haemoglobin value in g/L (internal storage format)
 * @param {string} locale - Country code (AU, US, GB, NZ, etc.)
 * @returns {number} Converted value for display
 * 
 * @example
 * convertHaemoglobinForDisplay(145, 'AU') // Returns: 145 (g/L)
 * convertHaemoglobinForDisplay(145, 'US') // Returns: 14.5 (g/dL)
 */
export const convertHaemoglobinForDisplay = (valueInGL, locale) => {
  if (valueInGL === null || valueInGL === undefined || isNaN(valueInGL)) {
    return null;
  }
  
  if (isSILocale(locale)) {
    return valueInGL; // Already in g/L
  }
  
  // US: Convert g/L to g/dL (divide by 10)
  return Math.round((valueInGL / 10) * 10) / 10; // Round to 1 decimal place
};

/**
 * Convert haemoglobin value from display format to storage format (g/L)
 * - US input in g/dL converts to g/L (multiply by 10)
 * - SI input is already in g/L
 * 
 * @param {number} displayValue - Haemoglobin value as entered by user
 * @param {string} locale - Country code
 * @returns {number} Value in g/L for storage
 * 
 * @example
 * convertHaemoglobinForStorage(145, 'AU') // Returns: 145 (already g/L)
 * convertHaemoglobinForStorage(14.5, 'US') // Returns: 145 (g/L)
 */
export const convertHaemoglobinForStorage = (displayValue, locale) => {
  if (displayValue === null || displayValue === undefined || isNaN(displayValue)) {
    return null;
  }
  
  if (isSILocale(locale)) {
    return displayValue; // Already in g/L
  }
  
  // US: Convert g/dL to g/L (multiply by 10)
  return Math.round(displayValue * 10);
};

// ============================================================================
// MARKER DEFINITIONS - Blood markers with their unit configurations
// ============================================================================

export const MARKERS = {
  wbc: {
    label: 'WBC',
    fullName: 'White Blood Cells',
    getUnit: () => UNITS.BLOOD_COUNT,
  },
  lymphocytes: {
    label: 'Lymphocytes',
    fullName: 'Lymphocytes',
    getUnit: () => UNITS.BLOOD_COUNT,
  },
  platelets: {
    label: 'Platelets',
    fullName: 'Platelets',
    getUnit: () => UNITS.BLOOD_COUNT,
  },
  neutrophils: {
    label: 'Neutrophils',
    fullName: 'Neutrophils',
    getUnit: () => UNITS.BLOOD_COUNT,
  },
  haemoglobin: {
    label: 'Haemoglobin',
    fullName: 'Haemoglobin',
    getUnit: (locale) => isSILocale(locale) ? UNITS.HEMOGLOBIN_SI : UNITS.HEMOGLOBIN_US,
  },
  vitamin_d: {
    label: 'Vitamin D',
    fullName: 'Vitamin D',
    getUnit: (locale) => isSILocale(locale) ? UNITS.VITAMIN_D_SI : UNITS.VITAMIN_D_US,
  },
  b12: {
    label: 'B12',
    fullName: 'Vitamin B12',
    getUnit: (locale) => isSILocale(locale) ? UNITS.B12_SI : UNITS.B12_US,
  },
  ferritin: {
    label: 'Ferritin',
    fullName: 'Ferritin',
    getUnit: () => UNITS.FERRITIN,
  },
  folate: {
    label: 'Folate',
    fullName: 'Folate',
    getUnit: (locale) => isSILocale(locale) ? UNITS.FOLATE_SI : UNITS.FOLATE_US,
  },
  iron: {
    label: 'Iron',
    fullName: 'Iron',
    getUnit: (locale) => isSILocale(locale) ? UNITS.IRON_SI : UNITS.IRON_US,
  },
};

// ============================================================================
// MAIN FORMATTER FUNCTION
// ============================================================================

/**
 * Get the formatted unit string for a marker
 * 
 * @param {string} marker - The marker key (e.g., 'wbc', 'haemoglobin')
 * @param {string} locale - Country code (AU, US, GB, NZ)
 * @param {string} output - Output type: 'display' for UI, 'pdf' for PDF export
 * @returns {string} The formatted unit string
 * 
 * @example
 * formatUnit('wbc', 'AU')           // Returns: "x10^9/L"
 * formatUnit('haemoglobin', 'AU')   // Returns: "g/L"
 * formatUnit('haemoglobin', 'US')   // Returns: "g/dL"
 */
export const formatUnit = (marker, locale = 'AU', output = 'display') => {
  const markerConfig = MARKERS[marker?.toLowerCase()];
  
  if (!markerConfig) {
    console.warn(`Unknown marker: ${marker}. Using default blood count unit.`);
    return UNITS.BLOOD_COUNT;
  }
  
  const unit = markerConfig.getUnit(locale);
  
  // Both display and PDF use the same format now (no Unicode superscripts)
  // This ensures consistency between UI and exports
  return unit;
};

/**
 * Get unit with label formatted for display
 * 
 * @param {string} marker - The marker key
 * @param {string} locale - Country code
 * @returns {string} Label with unit, e.g., "WBC (x10^9/L)"
 */
export const formatLabelWithUnit = (marker, locale = 'AU') => {
  const markerConfig = MARKERS[marker?.toLowerCase()];
  if (!markerConfig) return marker;
  
  const unit = formatUnit(marker, locale);
  return `${markerConfig.label} (${unit})`;
};

/**
 * Get the full marker configuration including unit
 * 
 * @param {string} marker - The marker key
 * @param {string} locale - Country code
 * @returns {Object} Full marker config with unit
 */
export const getMarkerConfig = (marker, locale = 'AU') => {
  const markerConfig = MARKERS[marker?.toLowerCase()];
  if (!markerConfig) return null;
  
  return {
    key: marker,
    label: markerConfig.label,
    fullName: markerConfig.fullName,
    unit: formatUnit(marker, locale),
  };
};

/**
 * Get all blood marker configurations for charts
 * 
 * @param {string} locale - Country code
 * @returns {Array} Array of marker configurations for chart rendering
 */
export const getBloodMarkerConfigs = (locale = 'AU') => {
  return [
    { 
      key: 'wbc', 
      label: 'WBC', 
      color: '#0F766E', 
      unit: formatUnit('wbc', locale), 
      range: { min: 4.0, max: 11.0 } 
    },
    { 
      key: 'lymphocytes', 
      label: 'Lymphocytes', 
      color: '#059669', 
      unit: formatUnit('lymphocytes', locale), 
      range: { min: 1.0, max: 4.0 } 
    },
    { 
      key: 'platelets', 
      label: 'Platelets', 
      color: '#0369A1', 
      unit: formatUnit('platelets', locale), 
      range: { min: 150, max: 400 } 
    },
    { 
      key: 'haemoglobin', 
      label: 'Haemoglobin', 
      color: '#7C3AED', 
      unit: formatUnit('haemoglobin', locale), 
      range: isSILocale(locale) ? { min: 120, max: 160 } : { min: 12.0, max: 16.0 }
    },
    { 
      key: 'neutrophils', 
      label: 'Neutrophils', 
      color: '#BE123C', 
      unit: formatUnit('neutrophils', locale), 
      range: { min: 2.0, max: 7.5 } 
    },
  ];
};

// ============================================================================
// REFERENCE RANGES BY LOCALE
// ============================================================================

/**
 * Get reference ranges for a locale
 * 
 * @param {string} locale - Country code
 * @returns {Object} Reference ranges with units
 */
export const getReferenceRanges = (locale = 'AU') => {
  const useSI = isSILocale(locale);
  
  return {
    wbc: { min: 4.0, max: 11.0, unit: UNITS.BLOOD_COUNT },
    lymphocytes: { min: 1.0, max: 4.0, unit: UNITS.BLOOD_COUNT },
    platelets: { min: 150, max: 400, unit: UNITS.BLOOD_COUNT },
    haemoglobin: useSI 
      ? { min: 120, max: 160, unit: UNITS.HEMOGLOBIN_SI }
      : { min: 12.0, max: 16.0, unit: UNITS.HEMOGLOBIN_US },
    neutrophils: { min: 2.0, max: 7.5, unit: UNITS.BLOOD_COUNT },
  };
};

// Default export for convenience
export default {
  UNITS,
  MARKERS,
  formatUnit,
  formatLabelWithUnit,
  getMarkerConfig,
  getBloodMarkerConfigs,
  getReferenceRanges,
  isSILocale,
};
